import { Group, Panel, Separator } from 'react-resizable-panels'
import GridWindow from './GridWindow'
import './grid.css'

// Generic tiled-window renderer. Columns run left->right (horizontal group);
// each column stacks its windows top->bottom (vertical group). Sizes persist via
// the grid's SET_*_SIZES actions; the library drives live resizing. Minimized
// windows live in the dock.
//
// Feature-agnostic props:
//   grid                - the useWindowGrid() return value
//   renderWindow(win)   - the window body
//   getTitle(win)       - the window header title
//   renderMenuItems(win) - feature menu rows (edit/delete/...) for the "more" menu
//   empty               - placeholder shown when no windows are open
function WindowGrid({ grid, renderWindow, getTitle, renderMenuItems, empty }) {
  const {
    visibleColumns,
    activeId,
    totalWindows,
    focusWindow,
    minimizeWindow,
    closeWindow,
    setColumnSizes,
    setWindowSizes,
  } = grid

  const totalVisible = visibleColumns.reduce((n, col) => n + col.windows.length, 0)

  const chrome = (win) => (
    <GridWindow
      win={win}
      title={getTitle(win)}
      active={activeId === win.id}
      canMinimize={totalVisible > 1}
      canClose={totalWindows > 1}
      menuItems={renderMenuItems ? renderMenuItems(win) : null}
      onFocus={() => focusWindow(win.id)}
      onMinimize={() => minimizeWindow(win.id)}
      onClose={() => closeWindow(win.id)}
    >
      {renderWindow(win)}
    </GridWindow>
  )

  const hasWindows = visibleColumns.length > 0

  let main
  if (!hasWindows) {
    main = <div className="grid-empty">{empty}</div>
  } else {
    const colChildren = []
    visibleColumns.forEach((col, ci) => {
      if (ci > 0) {
        colChildren.push(<Separator key={`sep-${col.id}`} className="grid-handle grid-handle-col" />)
      }
      colChildren.push(
        <Panel key={col.id} id={col.id} className="grid-col" minSize="15%">
          <ColumnStack
            col={col}
            chrome={chrome}
            onWindowSizes={(sizes) => setWindowSizes(col.id, sizes)}
          />
        </Panel>
      )
    })

    const colLayout = {}
    visibleColumns.forEach((col) => {
      colLayout[col.id] = col.size
    })

    main = (
      <Group
        orientation="horizontal"
        className="grid-group"
        defaultLayout={colLayout}
        onLayoutChanged={setColumnSizes}
      >
        {colChildren}
      </Group>
    )
  }

  return (
    <div className="window-grid">
      <div className="window-grid-main">{main}</div>
    </div>
  )
}

// One column: a vertical stack of its (visible) windows.
function ColumnStack({ col, chrome, onWindowSizes }) {
  if (col.windows.length === 1) {
    return chrome(col.windows[0])
  }

  const rowChildren = []
  col.windows.forEach((win, wi) => {
    if (wi > 0) {
      rowChildren.push(<Separator key={`sep-${win.id}`} className="grid-handle grid-handle-row" />)
    }
    rowChildren.push(
      <Panel key={win.id} id={win.id} className="grid-row" minSize="15%">
        {chrome(win)}
      </Panel>
    )
  })

  const rowLayout = {}
  col.windows.forEach((win) => {
    rowLayout[win.id] = win.size
  })

  return (
    <Group
      orientation="vertical"
      className="grid-group"
      defaultLayout={rowLayout}
      onLayoutChanged={onWindowSizes}
    >
      {rowChildren}
    </Group>
  )
}

export default WindowGrid
